import React from 'react';
import {
  Box, Typography, Stack, FormControl, Select, MenuItem,
  FormControlLabel, Switch,
} from '@mui/material';

const navy = '#1e3a5f';
const selectSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const SwitchRow = ({ label, hint, checked, onChange }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Box>
      <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 500 }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>{hint}</Typography>}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: navy },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: navy },
      }}
    />
  </Stack>
);

/**
 * OmerSettingsStep
 * שלב הגדרות ספירת העומר בוויזרד.
 *
 * Props:
 *   formData  { nusach, showBracha, showFullNusach, showLeshemYichud, showPasuk, showAfterCount }
 *   onChange  (partial) => void
 */
const OmerSettingsStep = ({ formData, onChange }) => {
  const nusach           = formData?.nusach           ?? 'ashkenaz';
  const showBracha       = formData?.showBracha       ?? true;
  const showFullNusach   = formData?.showFullNusach   ?? true;
  const showLeshemYichud = formData?.showLeshemYichud ?? false;
  const showPasuk        = formData?.showPasuk        ?? 'none';
  const showAfterCount   = formData?.showAfterCount   ?? false;
  const showPreview      = formData?.showPreview      ?? false;

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        הגדרות ספירת העומר
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        בחר את הנוסח ואילו חלקים יוצגו בספירה.
      </Typography>

      {/* נוסח */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 0.5 }}>
          נוסח
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 1.5 }}>
          קובע את נוסח שורת הספירה — ״בָּעֹמֶר״ (אשכנז) או ״לָעֹמֶר״ (ספרד / מזרח).
        </Typography>
        <FormControl fullWidth size="small">
          <Select value={nusach} onChange={(e) => onChange({ nusach: e.target.value })} sx={selectSx}>
            <MenuItem value="ashkenaz">אשכנז — ״בָּעֹמֶר״</MenuItem>
            <MenuItem value="sephardic">ספרד — ״לָעֹמֶר״</MenuItem>
            <MenuItem value="mizrahi">עדות המזרח — ״לָעֹמֶר״</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* הגדרות ספירה */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 1.5 }}>
          מה להציג בספירה
        </Typography>
        <Stack spacing={1.5}>
          <SwitchRow
            label="ברכה"
            hint='״ברוך אתה ה׳ אלוהינו... על ספירת העומר״'
            checked={showBracha}
            onChange={(v) => onChange({ showBracha: v })}
          />
          <SwitchRow
            label="נוסח מלא — שבועות וימים"
            hint='״שֶׁהֵם X שָׁבוּעוֹת וְY יָמִים״'
            checked={showFullNusach}
            onChange={(v) => onChange({ showFullNusach: v })}
          />
          <SwitchRow
            label="לשם ייחוד"
            hint="הצג נוסח לשם ייחוד לפני הספירה"
            checked={showLeshemYichud}
            onChange={(v) => onChange({ showLeshemYichud: v })}
          />
        </Stack>
      </Box>

      {/* פסוק פתיחה */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 0.5 }}>
          פסוק פתיחה
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 1.5 }}>
          פסוק שיוצג לפני הספירה.
        </Typography>
        <FormControl fullWidth size="small">
          <Select value={showPasuk} onChange={(e) => onChange({ showPasuk: e.target.value })} sx={selectSx}>
            <MenuItem value="none">ללא פסוק</MenuItem>
            <MenuItem value="veSafartem">וּסְפַרְתֶּם לָכֶם — ויקרא כג:טו</MenuItem>
            <MenuItem value="shevaShabatot">שֶׁבַע שַׁבָּתוֹת תְּמִימֹת — ויקרא כג:טז</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* תפילות לאחר ספירה */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <SwitchRow
          label="תפילות לאחר ספירה"
          hint="הרחמן, אנא בכח, תהילים סז"
          checked={showAfterCount}
          onChange={(v) => onChange({ showAfterCount: v })}
        />
      </Box>

      {/* תצוגה מקדימה מחוץ לעונה */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
        <SwitchRow
          label="תצוגה מקדימה מחוץ לעונה"
          hint="הצג את יום א׳ של הספירה + ספירה לאחור עד תחילת העומר"
          checked={showPreview}
          onChange={(v) => onChange({ showPreview: v })}
        />
      </Box>
    </Box>
  );
};

export default OmerSettingsStep;
